/**
 * @file loader parser
 * @author cxtom (cxtom2008@gmail.com)
 */

import cacheCreator from 'lru-cache';
import hash from 'hash-sum';
import {isArray, isObject, camelCase} from 'lodash';
import path from 'path';
import {codeFrameColumns} from '@babel/code-frame';

/* eslint-disable no-console, max-len */

let cache = cacheCreator(50000);

const pathFuncs = [
    'jsPath', 'phpPath', 'resolveAssertUrl',
    'compileJSComponent', 'compilePHPComponent'
];

const levelMap = {
    error: 'Error',
    warn: 'Warning'
};

export default function ({
    source,
    filename,
    compilerOptions = {},
    sourceRoot = '',
    compiler,
    loaderContext
}) { // eslint-disable-line

    const filePath = path.join(sourceRoot, filename);
    const cacheKey = hash(sourceRoot + filename + source);
    let output = cache.get(cacheKey);

    if (output) {
        return output;
    }

    const logger = (level, block, ...args) => {

        if (args.length === 0) {
            return;
        }

        args = args.map(item => {
            if (isArray(item)) {
                return item.map((n, i) => `${i + 1}. ${n}`).join('\n');
            }
            else if (isObject(item) && item.location && item.message) {
                let codeFrame = codeFrameColumns(item.code, item.location, {highlightCode: true});
                return `${item.message}\n${codeFrame}`;
            }
            return item;
        });

        let msg = `
[atom-loader] ${level} when compiling block <${block}> in ${filePath}:
${args.join(' ')}\n`;

        loaderContext[`emit${levelMap[level]}`](msg);
    };

    for (const func of pathFuncs) {
        if (compilerOptions[func]) {
            const handler = compilerOptions[func];
            compilerOptions[func] = (...args) => handler.call(loaderContext, ...args, filePath);
        }
    }

    const {
        typescriptCompilerOptions = {}
    } = compilerOptions;

    compilerOptions = Object.assign(
        {
            content: source,
            mode: 'commonjs',
            strip: true,
            logger
        },
        compilerOptions,
        {
            typescriptCompilerOptions: {
                ...typescriptCompilerOptions,
                ts2phpOptions: {
                    ...(typescriptCompilerOptions.ts2phpOptions || {}),
                    getNamespace() {

                        if (
                            typescriptCompilerOptions.ts2phpOptions
                            && typeof typescriptCompilerOptions.ts2phpOptions.getNamespace === 'function'
                        ) {
                            return typescriptCompilerOptions.ts2phpOptions.getNamespace(path.join(sourceRoot, filename));
                        }

                        const names = sourceRoot
                            .split('/')
                            .filter(a => !!camelCase(a))
                            .map(camelCase);
                        names.push(camelCase(filename.replace(/\.atom$/, '')));
                        names.unshift('atom');
                        return names.join('\\');
                    }
                }
            }
        }
    );

    let {compiled, blocks, errors} = compiler.compile(
        compilerOptions,
        error => {
            if (error) {
                loaderContext.emitError(`[atom-loader] error in ${filePath}:\n${error.stack}`);
            }
        }
    );

    if (errors && errors.length > 0) {
        loaderContext.emitError(`[atom-loader] ${errors.length} errors in ${filePath}`);
    }

    output = {compiled, blocks};
    cache.set(cacheKey, output);

    return output;

}
